import React, { useRef, useState } from 'react';

import { Banner, Box, Button, Spinner, Text } from '@betterment/samba-react';

import Docusign, { DocusignSessionEndEventProps } from '../AdvisedSignup401kAgreementSignature/components/Docusign';

export interface AdvisedClientAgreementSignatureProps {
  docusignClientId?: string;
  docusignSignatureUrl: string;
  signed: boolean;
}

const SignedAgreementBanner: React.FC<{ variant: 'success' | 'warning' }> = ({ variant }) => {
  return (
    <Banner variant={variant} css={{ mt: 'space.300', mb: 'space.0' }}>
      <Banner.Content>
        <Text as="p">The plan adoption agreement is signed; please proceed to the next step.</Text>
      </Banner.Content>
    </Banner>
  );
};

const AdvisedClientAgreementSignature: React.FC<React.PropsWithChildren<AdvisedClientAgreementSignatureProps>> = ({
  docusignClientId,
  docusignSignatureUrl,
  signed: initialSigned,
}) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [isSigned, setIsSigned] = useState(initialSigned);
  const [sessionEndType, setSessionEndType] = useState('');

  const onSessionEnd = (event: DocusignSessionEndEventProps) => {
    setSessionEndType(event.sessionEndType);

    if (event.sessionEndType === 'signing_complete') {
      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
      setIsSigned(true);
    }
  };

  const hasError = sessionEndType !== '' && sessionEndType !== 'signing_complete';

  return (
    <>
      {!initialSigned && isSigned && <SignedAgreementBanner variant="success" />}
      {initialSigned && <SignedAgreementBanner variant="warning" />}
      {hasError && (
        <Banner variant="critical" css={{ mt: 'space.300', mb: 'space.0' }}>
          <Banner.Content>
            <Text as="p">
              {(sessionEndType === 'session_timeout' || sessionEndType === 'ttl_expired') && <>Session expired</>}
              {sessionEndType !== 'session_timeout' && sessionEndType !== 'ttl_expired' && <>Something went wrong</>}
              {', '}
              <Button variant="textPrimary" onClick={() => window.location.reload()}>
                please try again
              </Button>
            </Text>
          </Banner.Content>
        </Banner>
      )}
      {!initialSigned && docusignClientId && !hasError && (
        <>
          {!docusignSignatureUrl ? (
            <Box
              css={{
                alignItems: 'center',
                border: 'solid 1px #E5E5E5',
                borderRadius: 'medium',
                display: 'flex',
                flexDirection: 'column',
                marginTop: 'space.500',
                paddingBottom: 'space.800',
                paddingTop: 'space.800',
              }}
            >
              <Spinner variant="secondary" css={{ marginBottom: 'space.200' }} />
              <Text color="text.weak">Generating your documents. This may take a few minutes...</Text>
            </Box>
          ) : (
            <Docusign
              docusignClientId={docusignClientId}
              docusignSignatureURL={docusignSignatureUrl}
              onSessionEnd={onSessionEnd}
            />
          )}
        </>
      )}
      <Box css={{ justifySelf: 'flex-start' }}>
        <div className="sc-Actions">
          <button type="submit" name="commit" ref={submitButtonRef} value="Continue" disabled={!isSigned}>
            Continue
          </button>
        </div>
      </Box>
    </>
  );
};

export default AdvisedClientAgreementSignature;
